import { Box, Button, Checkbox, createStyles, IconButton, InputAdornment, Link, List, ListItem, ListItemIcon, ListItemText, ListSubheader, makeStyles, TextField, Theme, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import { AttachMoney, Delete, Timer } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import clsx from 'clsx';
import firebase from 'firebase/app';
import { useSnackbar } from "notistack";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from 'react-router-dom';

import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { FullscreenLoading } from "components/FullscreenLoading";
import { IndustryTypeSelect } from "components/IndustryTypeSelect";
import { useOrg, useOrgIdLocalStorage } from "components/OrgProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation, responsiveDrawerDeviceSize } from "components/PageNavigation";
import { analytics } from "FirebaseConfigs";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { useSyncedState } from "hooks/useSyncedState";
import { LocalStorageKeys } from "LocalStorageKeys";
import { createTemplate, createTemplateId, createTemplateSuggestion, promoteTemplateSuggestion, subscribeToTemplate, templateFrom } from "modelActions/PriceFieldTemplate";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppRoles } from "models/AppRoles";
import { AppUser } from "models/AppUser";
import { DbDoc, WithRef } from "models/FbDoc";
import { Grant } from "models/Grant";
import { IndustryTypes } from "models/IndustryTypes";
import { Nullable } from "models/Nullable";
import { OrgRoles } from "models/OrgRoles";
import { getOrgUserGrants, OrgUser, orgUserIdFrom } from "models/OrgUser";
import { PriceField } from "models/PriceField";
import { PriceFieldCategories } from "models/PriceFieldCategories";
import { PriceFieldTemplate } from "models/PriceFieldTemplate";
import { EnumPresentMap, getEnumPresentValues } from "models/PropertyMap";
import { textValueFrom } from "models/TextValue";
import { WithoutId } from "models/WithoutId";
import { Paths } from "Paths";
import { Entry } from "screens/ListScreen";
import { HourRateInput } from "screens/MyOrgScreen";
import { createdByItem } from "screens/Offer/OfferScreen";
import { handleEnterKey } from "Util/handleEnterKey";
import { isEmpty } from "Util/isEmpty";
import { removeUndefined } from "Util/removeUndefined";

import { EditProps, FormGetter, ListEntryScreen } from "../ListEntryScreen";
import { NoSubscriptionAlert } from "components/NoSubscriptionAlert";
import { useSubscriptionAccess } from "hooks/useSubscriptionStatus";

type Props<T extends Entry> = {
    id?: string
    createNew?: boolean
    isTemplate?: boolean
    isSuggestion?: boolean
    singular: string
    saved?: PriceField | WithoutId<PriceField>
    savedActual?: Nullable<WithRef<DbDoc<T>>>
    makeUpdates: (updates: Nullable<WithoutId<PriceField>>) => Nullable<WithoutId<T>>
    title: string
    listPath: Paths
    collection: Collections
    createEvent: AnalyticsEvent
    createErrorEvent: AnalyticsEvent
    copyEvent: AnalyticsEvent
    copyErrorEvent: AnalyticsEvent
    deleteEvent: AnalyticsEvent
    deleteErrorEvent: AnalyticsEvent
    updateEvent: AnalyticsEvent
    updateErrorEvent: AnalyticsEvent
    addCategoryEvent: AnalyticsEvent
    removeCategoryEvent: AnalyticsEvent
    links?: React.ReactNode
    loadingSaved?: boolean
    createNewTitle: string
    createNewText: string
}

export function hasChanges<T>(source?: Nullable<T>, updates?: Nullable<Partial<T>>): boolean {
    if (updates === null || updates === undefined || source === null || source === undefined) {
        return updates !== source
    }
    if (typeof updates === 'object') {
        return Object
            .entries(updates)
            .some(([k, v]) => {
                //@ts-ignore
                return hasChanges(source[k], v)
            })
    }
    if (Array.isArray(updates)) {
        //TODO: support diffcheck for arrays
        console.error('missing support for arrays')
        analytics.logEvent('MISSING_ARRAY_SUPPORT')
        return true
    }
    return source !== updates
}

type PriceFieldCategorySelectorProps = {
    categories: EnumPresentMap<PriceFieldCategories.Types>
    industryTypes: EnumPresentMap<IndustryTypes.IndustryTypes>
    setCategories: (categories: EnumPresentMap<PriceFieldCategories.Types>) => void
    createNew?: boolean
    id?: string
    orgUser: Nullable<OrgUser>
    appUser: Nullable<AppUser>
    canEdit: boolean
    isSmall?: boolean
    collection: Collections
    errorEvent: AnalyticsEvent
    updateEvent: AnalyticsEvent
    isTemplate?: boolean
    addCategoryEvent: AnalyticsEvent
    removeCategoryEvent: AnalyticsEvent
}

const useStyles = makeStyles((theme: Theme) => createStyles({
    alertTitle: {
        width: '100%',
    },
    alertMessage: {
        width: '100%',
    },
}))

export function PriceFieldCategorySelector({
    categories,
    setCategories,
    industryTypes,
    id,
    createNew,
    orgUser,
    appUser,
    canEdit,
    isSmall,
    collection,
    errorEvent,
    updateEvent,
    isTemplate,
    addCategoryEvent,
    removeCategoryEvent,
}: PriceFieldCategorySelectorProps) {
    const { enqueueSnackbar } = useSnackbar()
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(responsiveDrawerDeviceSize), { noSsr: true })
    const [alertOpen, setAlertOpen] = useState(() => !!localStorage.getItem(LocalStorageKeys.CORRECT_PRICE_FIELD_CATEGORY_USE_ALERT_OPEN))
    const noWrap = !!(isMobile || isSmall) && !alertOpen
    const classes = useStyles({
        noWrap,
        alertOpen,
    })

    return (
        <List subheader={<ListSubheader disableSticky>Kategorier</ListSubheader>}>
            {(!createNew && !Object.values(categories).length) ? (
                <ListItem>
                    <Alert style={{ width: '100%' }} severity="warning">
                        Ingen kategorier valgt. Prisfeltet vil være usynlig i anbudsskjermen!
                    </Alert>
                </ListItem>
            ) : null}
            <ListItem>
                <Alert className={clsx(classes.alertTitle, classes.alertMessage)} classes={{ message: classes.alertMessage }} severity="info">
                    <div style={{ justifyContent: 'space-between', flexDirection: 'row', display: 'flex', paddingRight: noWrap ? theme.spacing(3) : undefined }}>
                        <Typography noWrap={noWrap}>
                            Et prisfelt vil vanligvis passe inn i flere Kategorier, og når det er tilfelle burde det inkluderes i alle.
                            For eksempel vil en våtromskontakt passe inn i bad, "Bad", "Våtrom", "Uteområde" og "Kontakter"
                            Prisfeltene blir fordelt i kategoritreet automatisk, så ikke vær redd for å overfylle kategoriene.
                        </Typography>
                        {(isMobile || isSmall) ? (
                            <Button
                                color="secondary"
                                size="small"
                                style={{ paddingTop: 0 }}
                                onClick={() => {
                                    if (alertOpen) {
                                        localStorage.removeItem(LocalStorageKeys.CORRECT_PRICE_FIELD_CATEGORY_USE_ALERT_OPEN)
                                    } else {
                                        localStorage.setItem(LocalStorageKeys.CORRECT_PRICE_FIELD_CATEGORY_USE_ALERT_OPEN, 'true')
                                    }
                                    setAlertOpen(!alertOpen)
                                }}
                            >{alertOpen ? 'Mindre' : 'Mer'}</Button>
                        ) : null}
                    </div>
                </Alert>
            </ListItem>
            {(
                IndustryTypes.FlatEnumList.filter(industryType => !!industryTypes[industryType]).map((industryType) => (
                    <>
                        {Object.keys(industryTypes).length > 1 ? (
                            <ListSubheader>{IndustryTypes.Flat[industryType].name}</ListSubheader>
                        ) : null}
                        {
                            PriceFieldCategories.GroupedList[industryType].map((definition) => {
                                const isSelected = !!categories[definition.id]
                                function toggleSelected() {
                                    const isCreateNew = !id || createNew
                                    const analyticsData = {
                                        orgId: orgUser?.org.id,
                                        category: definition.id,
                                        isCreateNew,
                                    }
                                    if (isSelected) {
                                        analytics.logEvent(AnalyticsEvent.ADDED_CATEGORY_TO_PRICE_FIELD, analyticsData)
                                    } else {
                                        analytics.logEvent(AnalyticsEvent.REMOVED_CATEGORY_FROM_PRICE_FIELD, analyticsData)
                                    }
                                    if (isCreateNew) {
                                        setCategories(removeUndefined({
                                            ...categories,
                                            [definition.id]: isSelected ? undefined : true
                                        }))
                                    } else if (id) {
                                        if (!orgUser) {
                                            console.error('No orgUser state!')
                                            return
                                        }
                                        const newValue = !isSelected || firebase.firestore.FieldValue.delete()

                                        if (isTemplate) {
                                            FirebaseUtils.updateDocument<PriceFieldTemplate>(
                                                true,
                                                collection || Collections.PRICE_FIELDS,
                                                id,
                                                appUser,
                                                orgUser,
                                                !isSelected ? (addCategoryEvent || AnalyticsEvent.ADDED_CATEGORY_TO_PRICE_FIELD) : (removeCategoryEvent || AnalyticsEvent.REMOVED_CATEGORY_FROM_PRICE_FIELD),
                                                updateEvent || AnalyticsEvent.UPDATED_PRICE_FIELD,
                                                errorEvent || AnalyticsEvent.UPDATE_PRICE_FIELD_ERROR,
                                                enqueueSnackbar,
                                                !isSelected ? 'Kategori lagt til' : 'Kategori fjernet',
                                                {
                                                    template: {
                                                        priceField: {
                                                            categories: {
                                                                map: {
                                                                    [definition.id]: newValue,
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                                () => ({ category: definition.id, priceFieldId: id }),
                                            )
                                        } else {
                                            FirebaseUtils.updateDocument<PriceField>(
                                                true,
                                                collection || Collections.PRICE_FIELDS,
                                                id,
                                                appUser,
                                                orgUser,
                                                !isSelected ? addCategoryEvent : removeCategoryEvent,
                                                updateEvent,
                                                errorEvent,
                                                enqueueSnackbar,
                                                !isSelected ? 'Kategori lagt til' : 'Kategori fjernet',
                                                {
                                                    categories: {
                                                        map: {
                                                            [definition.id]: newValue,
                                                        },
                                                    },
                                                },
                                                () => ({ category: definition.id, priceFieldId: id }),
                                            )
                                        }
                                    }
                                }

                                const checkId = "check-" + definition.name.toLowerCase().replaceAll(' ', '-')

                                return (
                                    <ListItem
                                        key={definition.name}
                                        button
                                        onClick={toggleSelected}
                                        divider
                                        disabled={!canEdit}
                                        dense
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                checked={isSelected}
                                                edge="start"
                                                tabIndex={-1}
                                                disableRipple
                                                disabled={!canEdit}
                                                inputProps={{ 'aria-labelledby': checkId }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText id={checkId} primary={definition.name} secondary={definition.description} />
                                    </ListItem>
                                )

                            })
                        }
                    </>
                ))
            )}
        </List>
    )
}

const emptyObject: EnumPresentMap<PriceFieldCategories.Types> = {}

type ExtraInputProps = {
    canEdit: boolean
    expectedTime: Nullable<string>
    materialPrice: Nullable<string>
    setExpectedTime: (value: Nullable<string>) => void
    setMaterialPrice: (value: Nullable<string>) => void
}

function toNum(value: Nullable<string>) {
    return isEmpty(value) ? 0 : Number(value)
}

export function ExtraInputs({ expectedTime, materialPrice, setExpectedTime, setMaterialPrice, canEdit }: ExtraInputProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(responsiveDrawerDeviceSize), { noSsr: true })
    const [org] = useOrg()
    const [orgUser] = useOrgUser()
    const [appUser] = useAppUser()
    const isOwner = orgUser?.data.orgRoles.map[OrgRoles.FlatEnum.OWNER]
    return (
        <>
            {canEdit ? (
                <ListSubheader disableSticky>{((materialPrice === null) || (expectedTime === null)) ? 'Legg til' : null}
                    {materialPrice === null ? (
                        isMobile ? (
                            <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => {
                                    setMaterialPrice('')
                                }}>
                                {<AttachMoney />}
                            </IconButton>
                        ) : (
                            <Box mx={1} display="inline">
                                <Button size="small" color="secondary" variant="outlined" endIcon={<AttachMoney />} onClick={() => {
                                    setMaterialPrice('')
                                }}>
                                    Materiell pris
                                </Button>
                            </Box>
                        )
                    ) : null}
                    {expectedTime === null ? (
                        isMobile ? (
                            <IconButton size="small" color="secondary" onClick={() => {
                                setExpectedTime('')
                            }}>
                                {<Timer />}
                            </IconButton>
                        ) : (
                            <Box mx={1} display="inline">
                                <Button size="small" color="secondary" variant="outlined" endIcon={<Timer />} onClick={() => {
                                    setExpectedTime('')
                                }}>
                                    Monteringstid
                                </Button>
                            </Box>
                        )
                    ) : null}
                </ListSubheader>
            ) : null}
            {materialPrice === null ? null : (
                <ListItem>
                    <TextField
                        type="number"
                        variant="outlined"
                        value={materialPrice || ''}
                        onChange={e => setMaterialPrice(e.target.value)}
                        label={'Materiell kostnad'}
                        disabled={!canEdit}
                        fullWidth
                        autoFocus={materialPrice === ''}
                        InputProps={canEdit ? {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton size="small" color="secondary" onClick={() => setMaterialPrice(null)}>
                                        <Delete />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        } : {}}
                    />
                </ListItem >
            )}
            {expectedTime === null ? null : (
                <>
                    <ListItem>
                        <TextField
                            type="number"
                            variant="outlined"
                            value={expectedTime || ''}
                            onChange={e => setExpectedTime(e.target.value)}
                            label={'Monteringstid (minutter)'}
                            disabled={!canEdit}
                            fullWidth
                            autoFocus={expectedTime === '' && !isMobile}
                            InputProps={canEdit ? {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton size="small" color="secondary" onClick={() => setExpectedTime(null)}>
                                            <Delete />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button color="secondary" onClick={() => setExpectedTime(String(Math.max((toNum(expectedTime) - 15), 0) || ''))}>
                                            -15 min
                                        </Button>
                                        <Button color="secondary" onClick={() => setExpectedTime(String(toNum(expectedTime) + 15))}>
                                            +15 min
                                        </Button>
                                    </InputAdornment>
                                ),
                            } : {}}
                        />
                    </ListItem >
                    {(canEdit && appUser && orgUser && org && isOwner) ? (
                        <HourRateInput appUser={appUser.data} orgUser={orgUser.data} org={org.data} />
                    ) : null}
                </>
            )}
        </>
    )
}

function useTemplateOriginal(template: Nullable<WithRef<DbDoc<PriceFieldTemplate>>>): [original: Nullable<WithRef<DbDoc<PriceField>>>, loading: boolean] {
    const [appUserDoc, loadingAppUser] = useAppUser()

    const appUser = appUserDoc?.data || null

    const isAdmin = !!appUser?.appRoles.map[AppRoles.FlatEnum.ADMIN]

    const [orgUserForDoc, loadingOrgUserForDoc] = FirebaseUtils.useDocumentData<OrgUser>(
        true,
        Collections.ORG_USERS,
        (template && appUser) ? orgUserIdFrom(template.data.template.priceField.org.id, appUser.id) : null,
        AnalyticsEvent.GET_OWN_ORG_USER_ERROR,
    )

    const [original, loadingOriginal] = FirebaseUtils.useDocumentData<PriceField>(
        true,
        Collections.PRICE_FIELDS,
        (isAdmin || !!orgUserForDoc) ? (template?.data.original?.priceField?.id || null) : null,
        AnalyticsEvent.GET_PRICE_FIELD_ERROR,
    )

    return [original, loadingOrgUserForDoc || loadingOriginal || loadingAppUser]
}

export function PriceFieldTemplateSuggestionScreen({ createNew, id }: { createNew?: boolean, id?: string }) {
    const { enqueueSnackbar } = useSnackbar()
    const [appUserDoc] = useAppUser()
    const [orgUserDoc] = useOrgUser()

    const appUser = appUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const [savedTemplateSuggestionDoc, loadingSavedTemplateSuggestionDoc] = FirebaseUtils.useDocumentData<PriceFieldTemplate>(
        true,
        Collections.PRICE_FIELD_TEMPLATE_SUGGESTIONS,
        (!createNew && id) ? id : null,
        AnalyticsEvent.GET_PRICE_FIELD_TEMPLATE_ERROR
    )

    const [savedTemplateDoc, loadingSavedTemplateDoc] = FirebaseUtils.useDocumentData<PriceFieldTemplate>(
        true,
        Collections.PRICE_FIELD_TEMPLATES,
        id || null,
        AnalyticsEvent.GET_PRICE_FIELD_TEMPLATE_ERROR
    )

    const [savedDoc, loadingSaved] = useTemplateOriginal(savedTemplateSuggestionDoc)

    const canCreateTemplate = !!appUser?.appRoles.map[AppRoles.FlatEnum.ADMIN]

    const updateBuilder = useCallback((v) =>
        v && ({
            ...savedTemplateSuggestionDoc?.data,
            template: {
                priceField: v,
            },
            original: savedTemplateSuggestionDoc?.data.original || null,
        }), [savedTemplateSuggestionDoc])

    if (!savedTemplateSuggestionDoc && !!savedTemplateDoc) {
        return (
            <PageNavigation title="Forslag til Prisfelt-Mal" backHistoryButton>
                <List>
                    <ListItem
                        component={RouterLink}
                        to={Paths.PRICE_FIELD_TEMPLATES + '/' + savedTemplateDoc?.data.id}
                    >
                        <ListItemText
                            primary="Dette forslaget har blitt godkjent"
                            secondary="Trykk her for å gå til malen"
                        />
                    </ListItem>
                </List>
            </PageNavigation>
        )
    }

    const links = (
        <>
            {savedDoc ? (
                <ListItem dense>
                    <Link component={RouterLink} to={Paths.PRICE_FIELDS + '/' + savedTemplateSuggestionDoc?.data.original?.priceField.id}>Dette forslaget er basert på ett prisfelt</Link>
                </ListItem>
            ) : null}
            {(!!savedTemplateSuggestionDoc && canCreateTemplate && appUser) ? (
                <ListItem>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => {
                            promoteTemplateSuggestion(
                                appUser,
                                orgUser,
                                savedTemplateSuggestionDoc.data,
                                enqueueSnackbar,
                            )
                        }}
                    >Godkjenn mal</Button>
                </ListItem>
            ) : null}
        </>
    )

    return (
        <GenericPriceFieldScreen<PriceFieldTemplate>
            id={id}
            createNew={!!createNew}
            isTemplate
            saved={savedTemplateSuggestionDoc?.data.template.priceField}
            savedActual={savedTemplateSuggestionDoc}
            singular="forslag"
            links={links}
            title="Forslag til Prisfelt-Mal"
            createNewTitle="Lag ett nytt forslag"
            createNewText="Trykk her for å lage ett forslag"
            makeUpdates={updateBuilder}
            isSuggestion
            loadingSaved={loadingSaved || loadingSavedTemplateSuggestionDoc || loadingSavedTemplateDoc}
            listPath={Paths.PRICE_FIELD_TEMPLATE_SUGGESTIONS}
            collection={Collections.PRICE_FIELD_TEMPLATE_SUGGESTIONS}
            createEvent={AnalyticsEvent.CREATED_PRICE_FIELD_TEMPLATE_SUGGESTION}
            createErrorEvent={AnalyticsEvent.CREATE_PRICE_FIELD_TEMPLATE_SUGGESTION_ERROR}
            copyEvent={AnalyticsEvent.COPIED_PRICE_FIELD_TEMPLATE_SUGGESTION}
            copyErrorEvent={AnalyticsEvent.COPIED_PRICE_FIELD_TEMPLATE_SUGGESTION_ERROR}
            deleteEvent={AnalyticsEvent.REMOVED_PRICE_FIELD_TEMPLATE_SUGGESTION}
            deleteErrorEvent={AnalyticsEvent.DELETE_PRICE_FIELD_TEMPLATE_SUGGESTION_ERROR}
            updateEvent={AnalyticsEvent.UPDATED_PRICE_FIELD_TEMPLATE_SUGGESTION}
            updateErrorEvent={AnalyticsEvent.UPDATE_PRICE_FIELD_TEMPLATE_SUGGESTION_ERROR}
            addCategoryEvent={AnalyticsEvent.ADDED_CATEGORY_TO_PRICE_FIELD_TEMPLATE_SUGGESTION}
            removeCategoryEvent={AnalyticsEvent.REMOVED_CATEGORY_FROM_PRICE_FIELD_TEMPLATE_SUGGESTION}
        />
    )
}

export function PriceFieldTemplateScreen({ createNew, id }: { createNew?: boolean, id?: string }) {
    const [savedTemplateDoc, loadingSavedTemplateDoc] = FirebaseUtils.useDocumentData<PriceFieldTemplate>(
        true,
        Collections.PRICE_FIELD_TEMPLATES,
        (!createNew && id) ? id : null,
        AnalyticsEvent.GET_PRICE_FIELD_TEMPLATE_ERROR
    )

    const [savedDoc, loadingSaved] = useTemplateOriginal(savedTemplateDoc)

    const links = (
        <>
            {savedDoc ? (
                <ListItem dense>
                    <Link component={RouterLink} to={Paths.PRICE_FIELDS + '/' + savedTemplateDoc?.data.original?.priceField.id}>Denne malen er basert på ett prisfelt</Link>
                </ListItem>
            ) : null}
        </>
    )

    return (
        <GenericPriceFieldScreen<PriceFieldTemplate>
            id={id}
            createNew={!!createNew}
            isTemplate
            saved={savedTemplateDoc?.data.template.priceField}
            savedActual={savedTemplateDoc}
            singular="mal"
            title="Prisfelt-Mal"
            links={links}
            createNewTitle="Lag en ny mal"
            makeUpdates={useCallback((v) =>
                v && ({
                    ...savedTemplateDoc?.data,
                    template: {
                        priceField: v
                    },
                    original: savedTemplateDoc?.data.original || null,
                }), [savedTemplateDoc])}
            createNewText="Trykk her for å lage en ny mal"
            loadingSaved={loadingSaved || loadingSavedTemplateDoc}
            listPath={Paths.PRICE_FIELD_TEMPLATES}
            collection={Collections.PRICE_FIELD_TEMPLATES}
            createEvent={AnalyticsEvent.CREATED_PRICE_FIELD_TEMPLATE}
            createErrorEvent={AnalyticsEvent.CREATE_PRICE_FIELD_TEMPLATE_ERROR}
            copyEvent={AnalyticsEvent.COPIED_PRICE_FIELD_TEMPLATE}
            copyErrorEvent={AnalyticsEvent.COPIED_PRICE_FIELD_TEMPLATE_ERROR}
            deleteEvent={AnalyticsEvent.REMOVED_PRICE_FIELD_TEMPLATE}
            deleteErrorEvent={AnalyticsEvent.DELETE_PRICE_FIELD_TEMPLATE_ERROR}
            updateEvent={AnalyticsEvent.UPDATED_PRICE_FIELD_TEMPLATE}
            updateErrorEvent={AnalyticsEvent.UPDATE_PRICE_FIELD_TEMPLATE_ERROR}
            addCategoryEvent={AnalyticsEvent.ADDED_CATEGORY_TO_PRICE_FIELD_TEMPLATE}
            removeCategoryEvent={AnalyticsEvent.REMOVED_CATEGORY_FROM_PRICE_FIELD_TEMPLATE}
        />
    )
}

export function PriceFieldScreen({ createNew, id }: { createNew?: boolean, id?: string }) {
    const { enqueueSnackbar } = useSnackbar()
    const [appUserDoc] = useAppUser()
    const [orgUserDoc] = useOrgUser()

    const appUser = appUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const [savedDoc, loadingSaved] = FirebaseUtils.useDocumentData<PriceField>(
        true,
        Collections.PRICE_FIELDS,
        (!createNew && id) ? id : null,
        AnalyticsEvent.GET_PRICE_FIELD_ERROR,
    )


    const isAdmin = !!appUser?.appRoles.map[AppRoles.FlatEnum.ADMIN]

    const [savedTemplateDoc, loadingSavedTemplateDoc] = FirebaseUtils.useDocumentData<PriceFieldTemplate>(
        true,
        Collections.PRICE_FIELD_TEMPLATES,
        savedDoc?.data.template?.id || null,
        AnalyticsEvent.GET_PRICE_FIELD_TEMPLATE_ERROR
    )

    const [savedTemplateSuggestionDoc, loadingSavedTemplateSuggestion] = FirebaseUtils.useDocumentData<PriceFieldTemplate>(
        true,
        Collections.PRICE_FIELD_TEMPLATE_SUGGESTIONS,
        savedDoc?.data.template?.id || null,
        AnalyticsEvent.GET_PRICE_FIELD_TEMPLATE_SUGGESTION_ERROR
    )

    const grants = getOrgUserGrants(orgUser)

    const canCreateTemplate = isAdmin
    const canCreateTemplateSuggestion = !!grants[Grant.CREATE_PRICE_FIELD]

    const links = (
        <>
            {savedTemplateDoc ? (
                <ListItem dense>
                    <Link
                        component={RouterLink}
                        to={Paths.PRICE_FIELD_TEMPLATES + '/' + savedTemplateDoc?.data.id}
                    >
                        {savedTemplateDoc.data.original?.priceField.id === savedDoc?.data.id ? 'En mal er opprettet basert på dette prisfeltet' : 'Dette prisfeltet er basert på en mal'}
                    </Link>
                </ListItem>
            ) : null}
            {savedTemplateSuggestionDoc ? (
                <ListItem dense>
                    <Link
                        component={RouterLink}
                        to={Paths.PRICE_FIELD_TEMPLATE_SUGGESTIONS + '/' + savedTemplateSuggestionDoc?.data.id}
                    >
                        {savedTemplateSuggestionDoc.data.original?.priceField.id === savedDoc?.data.id ? 'Ett forslag til en mal er opprettet basert på dette prisfeltet' : 'Dette prisfeltet er basert på ett forslag til en mal'}
                    </Link>
                </ListItem>
            ) : null}
            {(!savedTemplateSuggestionDoc && !savedTemplateDoc && !!savedDoc && !!appUser && (canCreateTemplate || canCreateTemplateSuggestion)) ? (
                <ListItem>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => {
                            const templateId = createTemplateId(true, !canCreateTemplate)
                            const template = templateFrom(templateId, savedDoc.data)
                            if (canCreateTemplate) {
                                createTemplate(
                                    appUser,
                                    orgUser,
                                    template,
                                    enqueueSnackbar,
                                )
                                subscribeToTemplate(
                                    savedDoc.data,
                                    appUser,
                                    orgUser,
                                    template,
                                    enqueueSnackbar,
                                    AnalyticsEvent.CREATED_PRICE_FIELD_TEMPLATE,
                                )
                            } else if (canCreateTemplateSuggestion) {
                                createTemplateSuggestion(
                                    appUser,
                                    orgUser,
                                    template,
                                    enqueueSnackbar,
                                )
                                subscribeToTemplate(
                                    savedDoc.data,
                                    appUser,
                                    orgUser,
                                    template,
                                    enqueueSnackbar,
                                    AnalyticsEvent.CREATED_PRICE_FIELD_TEMPLATE_SUGGESTION,
                                )
                            }
                        }}
                    >{canCreateTemplate ? 'Opprett mal' : 'Foreslå Mal'}</Button>
                </ListItem>
            ) : null}
        </>
    )

    return (
        <GenericPriceFieldScreen<PriceField>
            id={id}
            createNew={!!createNew}
            saved={savedDoc?.data}
            savedActual={savedDoc}
            singular="prisfelt"
            links={links}
            title="Prisfelt"
            createNewTitle="Lag ett nytt prisfelt"
            createNewText="Trykk her for å lage ett nytt prisfelt"
            makeUpdates={useCallback((v) => v, [])}
            listPath={Paths.PRICE_FIELDS}
            loadingSaved={loadingSaved || loadingSavedTemplateDoc || loadingSavedTemplateSuggestion}
            collection={Collections.PRICE_FIELDS}
            createEvent={AnalyticsEvent.CREATED_PRICE_FIELD}
            createErrorEvent={AnalyticsEvent.CREATE_PRICE_FIELD_ERROR}
            copyEvent={AnalyticsEvent.COPIED_PRICE_FIELD}
            copyErrorEvent={AnalyticsEvent.COPIED_PRICE_FIELD_ERROR}
            deleteEvent={AnalyticsEvent.REMOVED_PRICE_FIELD}
            deleteErrorEvent={AnalyticsEvent.DELETE_PRICE_FIELD_ERROR}
            updateEvent={AnalyticsEvent.UPDATED_PRICE_FIELD}
            updateErrorEvent={AnalyticsEvent.UPDATE_PRICE_FIELD_ERROR}
            addCategoryEvent={AnalyticsEvent.ADDED_CATEGORY_TO_PRICE_FIELD}
            removeCategoryEvent={AnalyticsEvent.REMOVED_CATEGORY_FROM_PRICE_FIELD}
        />
    )
}

//TODO: Make cloud function that deletes zonePriceFields after the price field or zone is deleted
function GenericPriceFieldScreen<T extends Entry>({
    id,
    saved,
    createNew,
    isTemplate,
    isSuggestion,
    singular,
    collection,
    createEvent,
    createErrorEvent,
    copyEvent,
    copyErrorEvent,
    deleteEvent,
    deleteErrorEvent,
    updateEvent,
    updateErrorEvent,
    addCategoryEvent,
    removeCategoryEvent,
    links,
    title,
    listPath,
    createNewText,
    createNewTitle,
    makeUpdates,
    loadingSaved,
    savedActual,
}: Props<T>) {
    const [subscriptionAccess] = useSubscriptionAccess()
    const [orgId] = useOrgIdLocalStorage()
    const [orgDoc, loadingOrg] = useOrg()
    const [orgUserDoc, loadingOrgUser] = useOrgUser()
    const [appUserDoc, loadingAppUser] = useAppUser()

    const orgUser = orgUserDoc?.data || null
    const appUser = appUserDoc?.data || null
    const org = orgDoc?.data || null

    const [orgUserForDoc, loadingOrgUserForDoc] = FirebaseUtils.useDocumentData<OrgUser>(
        true,
        Collections.ORG_USERS,
        (saved && appUser) ? orgUserIdFrom(saved.org.id, appUser.id) : null,
        AnalyticsEvent.GET_OWN_ORG_USER_ERROR,
    )

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(responsiveDrawerDeviceSize), { noSsr: true })

    const [loading] = useSyncedState(loadingSaved || loadingOrg || loadingOrgUser || loadingAppUser || loadingOrgUserForDoc);

    const [name, setName] = useSyncedState(saved?.name?.text || '');

    const [price, setPrice] = useSyncedState(!isEmpty(saved?.price) ? String(saved?.price) : '');
    const [materialPrice, setMaterialPrice] = useSyncedState(!isEmpty(saved?.materialPrice) ? String(saved?.materialPrice) : null);
    const [expectedTime, setExpectedTime] = useSyncedState(!isEmpty(saved?.expectedTime?.minutes) ? String(saved?.expectedTime?.minutes) : null);

    const isAdmin = !!appUser?.appRoles.map[AppRoles.FlatEnum.ADMIN]
    const grants = getOrgUserGrants(orgUser)
    const canCreate = !!appUser && !!orgUser && ((isTemplate && isSuggestion) || (!isTemplate && !!grants?.[Grant.CREATE_PRICE_FIELD]))
    const canEdit = (canCreate && !!createNew) || (!!appUser && ((isTemplate && isAdmin && !!orgUser) || (!!orgUser && ((isTemplate && createNew) || (!!grants?.[Grant.EDIT_PRICE_FIELD] && (saved?.org.id === orgUser.org.id) && !(isTemplate && !isSuggestion))))))
    const isOwnDoc = !!appUser && !!savedActual?.metadata.createdBy.appUser && (savedActual.metadata.createdBy.appUser.id === appUser.id)
    const isOwnSuggestion = isSuggestion && isOwnDoc
    const canDeleteInOrg = !!orgUser && (saved?.org.id === orgUserForDoc?.data.org.id) && !!grants?.[Grant.DELETE_PRICE_FIELD]
    const canDelete = !!appUser && ((isTemplate && (isAdmin || isOwnSuggestion)) || ((!isTemplate || !!isSuggestion) && canDeleteInOrg))

    const [categories, setCategories] = useSyncedState(saved?.categories.map || emptyObject)
    const [subCategories] = useSyncedState(saved?.subCategories.map || emptyObject)

    const categoryIndustryTypes: EnumPresentMap<IndustryTypes.IndustryTypes> = useMemo(() => [
        ...PriceFieldCategories.industryTypesFor(getEnumPresentValues(categories)),
        ...PriceFieldCategories.industryTypesFor(getEnumPresentValues(subCategories)),
    ].reduce((acc, key) => ({ ...acc, [key]: true }), {}), [categories, subCategories])

    const [industryTypes, setIndustryTypes] = useSyncedState(useMemo(() => ({ ...saved?.industryTypes.map, ...categoryIndustryTypes }), [saved, categoryIndustryTypes]))
    const industryTypeList = useMemo(() => Object.keys(industryTypes) as IndustryTypes.IndustryType[], [industryTypes])

    const priceAsNumber = Number(price)
    const materialAsNumber = Number(materialPrice);
    const timeAsNumber = Number(expectedTime);


    const nameInputRef = useRef<any>();
    const priceInputRef = useRef<any>();

    if (loading) {
        return <FullscreenLoading screenId="PriceFieldScreen" />
    }

    const updates: Nullable<WithoutId<PriceField>> = org && {
        org: {
            id: saved?.org.id || org.id,
        },
        type: null,
        name: textValueFrom(name),
        price: priceAsNumber,
        materialPrice: isEmpty(materialPrice) ? null : materialAsNumber,
        expectedTime: isEmpty(expectedTime) ? null : {
            minutes: timeAsNumber,
        },
        categories: { map: categories },
        subCategories: { map: subCategories },
        template: saved?.template || null,
        industryTypes: { map: industryTypes }
    }

    const missingCategories = !Object.values(categories).length

    const isChanged = hasChanges(saved, updates)

    const formValidWithoutChanges =
        !!name
        && !!priceAsNumber
        && !!categories
        && !isNaN(priceAsNumber)
        && (isEmpty(materialAsNumber) || !isNaN(materialAsNumber))
        && (isEmpty(expectedTime) || !isNaN(timeAsNumber))
        && !missingCategories

    const formIsValid = formValidWithoutChanges && isChanged && (!createNew || subscriptionAccess || !!isTemplate)

    const clearForm = () => {
        nameInputRef.current?.focus()
        setName('')
        setPrice('')
        setCategories(emptyObject)
        //setLoading(true)
    }

    const getForm: FormGetter = ({ save }) => (
        <>
            {createdByItem(savedActual?.metadata.createdBy.orgUser, isTemplate)}
            <ListItem>
                <TextField
                    autoFocus={!isMobile}
                    type="text"
                    variant="outlined"
                    fullWidth
                    label="Navn"
                    id="name"
                    aria-label="name"
                    autoComplete="off"
                    value={name}
                    disabled={!canEdit}
                    onChange={(e) => setName(e.target.value)}
                    inputRef={nameInputRef}
                    onKeyDown={handleEnterKey(() => {
                        priceInputRef.current?.focus()
                    })}
                />
            </ListItem>
            <ListItem>
                <TextField
                    type="number"
                    variant="outlined"
                    fullWidth
                    label="Pris"
                    id="price"
                    aria-label="price"
                    value={price}
                    disabled={!canEdit}
                    inputRef={priceInputRef}
                    onChange={(e) => setPrice(e.target.value)}
                    onKeyDown={handleEnterKey((e) => {
                        save()
                        //@ts-ignore
                        e.target.blur()
                    })}
                />
            </ListItem>
            {// TODO: Support creating templates without an org
                (false && isTemplate) ? (
                    <ListItem>
                        <IndustryTypeSelect industryType={(industryTypeList.length !== 1) ? null : industryTypeList[0]} setIndustryType={it => setIndustryTypes({ [it]: true })} />
                    </ListItem>
                ) : null}
            <ExtraInputs
                canEdit={canEdit}
                materialPrice={materialPrice}
                setMaterialPrice={setMaterialPrice}
                expectedTime={expectedTime}
                setExpectedTime={setExpectedTime}
            />
            {(missingCategories && createNew) ? (
                <ListItem dense>
                    <Typography variant="subtitle2" color="error">Du må velge minst en kategori før du kan lagre</Typography>
                </ListItem>
            ) : null}
            {(createNew && !isTemplate) ? <NoSubscriptionAlert showPaymentManager /> : null}
            {links}
            {orgId ? null : (
                <ListItem>
                    <Typography color="secondary">
                        {'For å gjøre endringer må du '}<Link href={Paths.SELECT_ORG}> velge en organisasjon</Link>
                    </Typography>
                </ListItem>
            )}
        </>
    )

    const editProps: EditProps = {
        formIsValid,
        clearForm,
        getForm,
    }

    return (
        <ListEntryScreen<T>
            requireOrgId={createNew}
            canEdit={canEdit}
            canCreate={canCreate}
            canDelete={canDelete}
            createProps={createNew ? {
                //goToOnCreated
                id,
                data: makeUpdates(updates),
                goToOnCreated: true,
                createEvent,
                createContextEvent: createEvent,
                createErrorEvent,
                createNewTitle,
                editProps,
            } : undefined}
            updateProps={(!id || createNew) ? undefined : {
                //goToOnCreated
                id,
                updates: makeUpdates(updates),
                updateEvent,
                updateContextEvent: updateEvent,
                updateErrorEvent,
                editProps,
            }}
            altCreateNewProps={{
                createNewText,
                createNewPath: Paths.CREATE_PRICE_FIELD,
            }}
            collectionName={collection}
            title={title}
            listPath={listPath}
            copyProps={{
                createEvent,
                copyContextEvent: copyEvent,
                copyErrorEvent,
            }}
            deleteProps={{
                deleteEvent,
                deleteErrorEvent,
                deleteContextEvent: deleteEvent,
            }}
            notFoundText={"Kunne ikke finne " + singular}
            singularText={singular}
            loading={loading}
            orgUser={orgUser}
            appUser={appUser}
            saved={savedActual?.data}
        >
            {(!loading ? (
                <PriceFieldCategorySelector
                    industryTypes={{ ...industryTypes, ...org?.industryTypes.map }}
                    canEdit={canEdit}
                    createNew={createNew}
                    id={id}
                    orgUser={orgUser}
                    appUser={appUser}
                    categories={categories}
                    isTemplate={isTemplate}
                    collection={collection}
                    updateEvent={updateEvent}
                    errorEvent={updateErrorEvent}
                    setCategories={setCategories}
                    addCategoryEvent={addCategoryEvent}
                    removeCategoryEvent={removeCategoryEvent}
                />
            ) : null)}
        </ListEntryScreen>
    )
}